<script setup lang="ts">
const props = defineProps<{
  data: BigHeadingProps
  chapterIndex?: number
  template?: 'page' | 'overlay'
}>()
const bigHeading = ref<HTMLElement>()
const contentWrapper = ref<HTMLElement>()
const chapterText = ref<HTMLElement>()
const mediaWrapper = ref<HTMLElement[]>()
const targetEndContainer = ref<HTMLElement>()
const itemRefs = ref<HTMLElement[]>([])
const portableTextContainer = ref<HTMLElement>()
const { $gsap, $Flip, $ScrollTrigger } = useNuxtApp()
let animationTimeline: gsap.core.Timeline | null = null
let introAnimationTimeline: gsap.core.Timeline | null = null
let flipCtx: gsap.Context | null = null
const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})

function findMediaItem(content: MediaContent[][][]) {
  for (const block of content) {
    if (block.line && Array.isArray(block.line)) {
      for (const item of block.line) {
        if (item._type === 'media') {
          return item
        }
      }
    }
  }
  return null
}

const textDominantColorFromImage = computed(() => {
  if (!props.data.text) return null
  return findMediaItem(props.data.text)?.image?.palette?.dominant?.foreground
})

const createIntroTimeline = () => {
  return new Promise<void>((resolve) => {
    if (!contentWrapper.value || isMobile.value) return resolve()
    introAnimationTimeline?.kill?.()
    introAnimationTimeline = $gsap.timeline({
      scrollTrigger: {
        id: 'big-heading-media-intro',
        trigger: bigHeading.value,
        scroller: document.querySelector('#overlay') ?? null,
        scrub: true,
        start: 'top bottom-=10%',
        end: 'top top',
        // markers: true,
      },
    })

    introAnimationTimeline!.fromTo(
      contentWrapper.value,
      {
        // opacity: 0,
        yPercent: -10,
      },
      {
        // opacity: 1,
        yPercent: 0,
        duration: 1.5,
        ease: 'power2.inOut',
        onComplete: () => {
          if (!contentWrapper.value) return resolve()
          $gsap.set(contentWrapper.value, { clearProps: 'all' })
          // contentWrapper.value.offsetHeight
          resolve()
        },
      },
    )
  })
}

async function createPinnedTimeline() {
  return new Promise<void>((resolve) => {
    if (!bigHeading.value && !mediaWrapper.value?.length && !contentWrapper.value) return
    const media = bigHeading.value?.querySelector('.media')
    if (!media) return

    flipCtx = $gsap.context(() => {
      if (!targetEndContainer.value) return
      const targetEndState = $Flip.getState(targetEndContainer.value)
      if (!contentWrapper.value) return
      animationTimeline = $gsap.timeline({
        scrollTrigger: {
          id: 'big-heading-media',
          trigger: bigHeading.value,
          scroller: document.querySelector('#overlay') ?? null,
          scrub: true,
          start: 'top top',
          anticipatePin: 1,
          end: 'center top',
          // markers: true,
          onUpdate: (self) => {
            if (self.progress >= 0.5 && portableTextContainer.value) {
              portableTextContainer.value.style.color =
                textDominantColorFromImage.value || 'inherit'
              if (chapterText.value)
                chapterText.value.style.color = textDominantColorFromImage.value || 'inherit'
            } else if (portableTextContainer.value) {
              portableTextContainer.value.style.color = 'inherit'
              if (chapterText.value) chapterText.value.style.color = 'inherit'
            }
          },
        },
        onComplete: () => {
          resolve()
        },
      })

      animationTimeline!
        .add(
          $Flip.fit(media, targetEndState, {
            ease: 'power2.inOut',
            duration: 1,
          }),
        )
        .to(
          media,
          {
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
            ease: 'power2.inOut',
            duration: 1,
          },
          '<',
        )
      const img = media.querySelector('img')
      if (img) {
        img.style.width = '100%'
        img.style.height = '100%'
      }
      if (!isMobile.value) {
        itemRefs.value?.forEach((el) => {
          const prev = el.previousElementSibling
          const next = el.nextElementSibling
          if (prev) {
            $gsap.set(prev, { zIndex: 2 })
            animationTimeline!.to(
              prev,
              {
                x: '0.4lh',
                ease: 'power2.inOut',
                duration: 0.5,
              },
              '<',
            )
          }
          if (next) {
            $gsap.set(next, { zIndex: 1 })
            animationTimeline!.to(
              next,
              {
                x: '-0.4lh',
                ease: 'power2.inOut',
                duration: 0.5,
              },
              '<',
            )
          }
        })
      }
    })
  })
}

// const debouncedSetup = useDebounceFn(async () => {
//   await createIntroTimeline()
//   await createPinnedTimeline()
// }, 200)
// watch(innerWidth, debouncedSetup)

onMounted(async () => {
  await nextTick()
  await cleanupAnimation()
  await createIntroTimeline()
  await createPinnedTimeline()
})

const { width } = useWindowSize()

watch(width, () => {
  setTimeout(async () => {
    await cleanupAnimation()
    await createIntroTimeline()
    await createPinnedTimeline()
  }, 1000)
})

async function cleanupAnimation(): Promise<void> {
  $ScrollTrigger.getById('big-heading-media')?.kill()
  $ScrollTrigger.getById('big-heading-media-intro')?.kill()
  animationTimeline?.kill?.()
  introAnimationTimeline?.kill?.()
  flipCtx?.revert()
  await Promise.resolve()
}

onBeforeUnmount(async () => {
  await cleanupAnimation()
})
</script>

<template>
  <section
    ref="bigHeading"
    class="relative flex flex-col items-center justify-center overflow-clip max-lg:min-h-screen max-lg:pt-32 lg:h-[200vh] lg:justify-start"
    :class="{
      'bg-white': template !== 'page' && template !== 'overlay' && data.theme !== 'light-grey',
      'bg-light-grey':
        template === 'page' || (template !== 'overlay' && data.theme === 'light-grey'),
    }"
  >
    <div
      ref="contentWrapper"
      class="relative z-1 flex w-full flex-col justify-center pb-6 transition-colors duration-1000 ease-in-out lg:sticky lg:top-0 lg:h-screen"
    >
      <div class="text-wrapper z-1 flex h-fit w-full flex-col items-center justify-center">
        <div
          v-if="chapterIndex && data.trumpet"
          ref="chapterText"
          class="chapter-text relative z-2 pb-6 text-center lg:pb-8"
        >
          <ElementsChapter :chapter="data.trumpet" :index="chapterIndex" />
        </div>
        <div
          ref="portableTextContainer"
          class="font-display relative m-auto flex max-w-full flex-col items-center text-center text-5xl uppercase font-stretch-expanded transition-all duration-300 ease-in-out lg:max-w-3xl xl:max-w-full xl:text-7xl"
        >
          <div v-for="(line, index) in data.text" :key="index" class="max-w-full">
            <div
              v-for="(inner, idx) in line"
              :key="idx"
              class="relative flex h-full max-w-full flex-col flex-wrap items-center justify-center gap-4 lg:inline-flex lg:flex-row lg:flex-nowrap lg:items-baseline"
            >
              <div
                v-for="(item, idxx) in inner"
                :ref="
                  (el) => {
                    if (item?.asset || item?.image || item?.video) {
                      itemRefs[idxx] = el as HTMLElement
                    }
                  }
                "
                :key="idxx"
                class="item relative max-w-full overflow-visible"
              >
                <p v-if="item.text" class="relative z-[2]">{{ item.text }}</p>
                <div
                  v-else-if="item.image || item.video"
                  class="media-wrapper relative z-[1] h-[4lh] w-[4lh] overflow-visible lg:h-[.8lh] lg:w-[.8lh]"
                >
                  <CommonMedia
                    :media="item"
                    :sizes="'60vw'"
                    class="h-[4lh] w-[4lh] overflow-clip lg:h-[.8lh] lg:w-[.8lh]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="target-container absolute top-0 right-0 bottom-0 left-0 h-full w-full max-lg:min-h-screen"
    >
      <div
        ref="targetEndContainer"
        class="absolute top-0 left-0 h-full w-full lg:sticky lg:h-screen"
      />
    </div>
    <div
      class="pointer-events-none relative bottom-0 z-2 ml-auto flex h-full items-end lg:absolute lg:right-0 lg:w-1/2"
    >
      <div class="bottom-0 flex flex-col lg:sticky lg:ml-auto lg:w-1/2">
        <ElementsOverlayButton
          v-for="(overlay, index) in data.overlays"
          :key="index"
          :overlay="overlay"
          class="lg:w-full"
        />
      </div>
    </div>
  </section>
</template>
<style scoped>
.media {
  clip-path: polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%);
}
</style>
